import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "./Navbar";
import { Container, Card, Table, Form, Button, Row, Col, Badge } from "react-bootstrap";
import { API_ENDPOINTS } from './config/api';
import Modal from 'react-bootstrap/Modal';
import PageTitle from './components/PageTitle';
import { getRatingOptions } from "./utils/ratingScales";


const EvaluationTemplateViewer = () => {
  const { id } = useParams();
  const [template, setTemplate] = useState(null);
  const [questions, setQuestions] = useState([]);
  const navigate = useNavigate();
  // Add useState for all residents
  const [allResidents, setAllResidents] = useState([]);
  const [showResidentsModal, setShowResidentsModal] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const questionRefs = useRef({});
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/evaluation-templates/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (res.ok) {
          const data = await res.json();
          setTemplate(data.template);
          setQuestions(data.questions.map(q => {
            const type = q.type || q.questionType;
            if (type === "header-paragraph") {
              try {
                const parsed = JSON.parse(q.questionText);
                return {
                  ...q,
                  type,
                  header: parsed.header,
                  paragraph: parsed.paragraph,
                  size: parsed.size || "h2",
                  id: q.id
                };
              } catch (e) {
                return {
                  ...q,
                  type,
                  header: q.questionText,
                  paragraph: "",
                  size: "h2",
                  id: q.id
                };
              }
            }
            return {
              ...q,
              label: q.label || q.questionText,
              type,
              options: q.options || [],
              id: q.id
            };
          }));
        }
      } catch (error) {
        console.error("Error fetching template:", error);
      }
    };

    fetchTemplate();
    // Fetch all residents for name lookup
    const fetchResidents = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setAllResidents(data.filter(user => user.role === 'resident'));
        }
      } catch (err) {
        console.error('Error fetching residents:', err);
      }
    };
    fetchResidents();
  }, [id]);



  if (!template) return <p>Loading...</p>;

  // Helper: get all numbered questions (exclude header-paragraph and divider)
  const numberedQuestions = questions.filter(q => !["header-paragraph", "divider"].includes(q.type));

  return (
    <div className="min-vh-100">
      <PageTitle
        title={template.title || "Evaluation Template"}
        description="View evaluation template details and questions"
      />
      <Navbar pageTitle={template.title || "Evaluation Template"} />
      <div className="bg-white shadow-sm custom-sticky-top">
        <Container>
          <div className="d-flex flex-md-row align-items-md-center align-items-start flex-column justify-content-md-between justify-content-start gap-2">
            <div className="d-flex flex-row align-items-center justify-content-start gap-2">
              <h5 className="fw-semibold mb-0 py-md-3 py-2">
                {template.title}
              </h5>
              <span className={`badge ${
                template.evaluationType === 'performance' ? 'bg-primary' : 
                template.evaluationType === 'feedback' ? 'bg-info' :
                template.evaluationType === 'survey' ? 'bg-success' :
                template.evaluationType === 'questionnaire' ? 'bg-warning' :
                'bg-secondary'
              }`}>
                {template.evaluationType || template.type || "Evaluation"}
              </span>
            </div>
            <div className="d-flex py-md-0 py-2 flex-row align-items-center justify-content-end gap-3">
              <Button
                variant="light-solid"
                size="sm"
                onClick={() => setShowDetails(!showDetails)}
                className="d-flex align-items-center gap-2"
              >
                <span className="d-none d-md-inline">{showDetails ? 'Hide' : 'Show'} Details</span>
                <i className={`bi ${showDetails ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
              </Button>
            </div>
          </div>
          {showDetails && (
            <div className="border-top pt-3 pb-3">
              <Row className="g-3">
                {template.isTimed && template.timeLimit && (
                  <Col md={4} lg={4} className="col-6">
                    <div>
                      <span className="text-muted fw-bold small d-block mb-1">Time Limit:</span>
                      <div>
                        <p className="mb-0 text-muted">{template.timeLimit} minutes</p>
                      </div>
                    </div>
                  </Col>
                )}
                {template.startTime && (
                  <Col md={4} lg={4} className="col-6">
                    <div>
                      <span className="text-muted fw-bold small d-block mb-1">Start Time:</span>
                      <div>
                        <p className="mb-0 text-muted">{new Date(template.startTime).toLocaleString()}</p>
                      </div>
                    </div>
                  </Col>
                )}
                {template.endTime && (
                  <Col md={4} lg={4} className="col-6">
                    <div>
                      <span className="text-muted fw-bold small d-block mb-1">End Time:</span>
                      <div>
                        <p className="mb-0 text-muted">{new Date(template.endTime).toLocaleString()}</p>
                      </div>
                    </div>
                  </Col>
                )}
                <Col md={4} lg={4} className="col-6">
                  <div>
                    <span className="text-muted fw-bold small d-block mb-1">Total Questions:</span>
                    <p className="mb-0 text-muted">{numberedQuestions.length}</p>
                  </div>
                </Col>
                {template.assignedResidents && template.assignedResidents.length > 0 && (
                  <Col md={4} lg={4} className="col-6">
                    <div>
                      <span className="text-muted fw-bold small d-block mb-1">Assigned Residents:</span>
                      <div>
                        <p className="mb-0 text-muted">{template.assignedResidents.length} resident{template.assignedResidents.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  </Col>
                )}
                {template.createdAt && (
                  <Col md={4} lg={4} className="col-6">
                    <div>
                      <span className="text-muted fw-bold small d-block mb-1">Created On:</span>
                      <div>
                        <p className="mb-0 text-muted">{new Date(template.createdAt).toLocaleString()}</p>
                      </div>
                    </div>
                  </Col>
                )}
                {template.updatedAt && template.updatedAt !== template.createdAt && (
                  <Col md={4} lg={4} className="col-6">
                    <div>
                      <span className="text-muted fw-bold small d-block mb-1">Last Updated:</span>
                      <div>
                        <p className="mb-0 text-muted">{new Date(template.updatedAt).toLocaleString()}</p>
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          )}
        </Container>
      </div>
      <Container className="py-4">
        <Row>
          <Col lg={3} md={4} className="d-none d-md-block">
            <div className="position-sticky" style={{ top: 80 }}>
              <Card className="border-0 shadow-sm rounded-4">
                <Card.Header className="border-0 bg-white px-4 pt-3 pb-2 fw-bold mb-2">
                  Questions
                </Card.Header>
                <Card.Body className="px-3 pt-0">
                  <ul className="list-unstyled mb-0">
                    {template?.introHtml && template.introHtml.trim() && (
                      <li key="intro" className="mb-2">
                        <Button
                          variant="light"
                          className="d-flex align-items-center justify-content-between p-2 rounded-3 border bg-white w-100"
                          style={{ overflow: "hidden" }}
                          onClick={() => {
                            const node = questionRefs.current["intro"];
                            if (node) {
                              node.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                              });
                            }
                          }}
                        >
                          <div className="d-flex align-items-center gap-3 text-start">
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center border text-muted"
                              style={{ width: 32, height: 32 }}
                            >
                              <i className="bi bi-info-lg text-muted" />
                            </div>
                            <div>
                              <div
                                className="fw-semibold text-truncate small"
                                style={{ maxWidth: 180 }}
                              >
                                Introduction
                              </div>
                            </div>
                          </div>
                        </Button>
                      </li>
                    )}
                    {numberedQuestions.map((question, idx) => {
                      const qId = question.id || `question-${idx}`;
                      return (
                        <li key={qId} className="mb-2">
                          <Button
                            variant="light"
                            className="d-flex align-items-center justify-content-between p-2 rounded-3 border bg-white w-100"
                            style={{ overflow: "hidden" }}
                            onClick={() => {
                              const node = questionRefs.current[qId];
                              if (node) {
                                node.scrollIntoView({
                                  behavior: "smooth",
                                  block: "center",
                                });
                              }
                            }}
                          >
                            <div className="d-flex align-items-center gap-3 text-start">
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center border text-muted"
                                style={{ width: 32, height: 32 }}
                              >
                                <span className="fw-semibold">{idx + 1}</span>
                              </div>
                              <div>
                                <div
                                  className="fw-semibold text-truncate small"
                                  style={{ maxWidth: 180 }}
                                >
                                  {question.label || question.questionText || `Question ${idx + 1}`}
                                </div>
                              </div>
                            </div>
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                </Card.Body>
              </Card>
            </div>
          </Col>
          <Col lg={9} md={8}>
            <Card className="mb-4 shadow-sm border-0">
              <Card.Body className="p-0">
                {template?.introHtml && (
                  <div
                    className="mb-4 mx-4 mt-4 p-3 rounded-3 bg-light border-0 eval-rich-content"
                    style={{ lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{ __html: template.introHtml }}
                    ref={(el) => {
                      if (el) {
                        questionRefs.current["intro"] = el;
                      }
                    }}
                  />
                )}
                {questions.map((q, i) => {
                  // Calculate question number (exclude header-paragraph and divider)
                  const questionNumber = questions.slice(0, i + 1).filter(qq => !["header-paragraph", "divider"].includes(qq.type)).length;
                  const qId = q.id || `question-${i}`;
                  
                  return (
                    <div
                      key={qId}
                      className="mb-5 pb-5 mt-4 px-4 eval-question-border"
                      ref={(el) => {
                        if (el) {
                          questionRefs.current[qId] = el;
                        }
                      }}
                    >
                      <div>
                        {q.type === "header-paragraph" ? (
                          <div>
                            <div
                              style={{
                                fontSize:
                                  q.size === "h1"
                                    ? "32px"
                                    : q.size === "h2"
                                    ? "24px"
                                    : "20px",
                                fontWeight: "bold",
                                marginBottom: "10px",
                                color: "var(--gray-800)",
                              }}
                            >
                              {q.header || "Header Text"}
                            </div>
                            <div
                              style={{
                                marginBottom: "20px",
                                color: "var(--muted-text)",
                                lineHeight: "1.5",
                              }}
                            >
                              {q.paragraph || "Paragraph text goes here..."}
                            </div>
                          </div>
                        ) : q.type === "divider" ? (
                          <hr
                            style={{
                              borderTop: `2px ${q.style || "solid"} #ccc`,
                              margin: "32px 0",
                            }}
                          />
                        ) : (
                          <>
                            <div className="d-flex align-items-center gap-3 mb-4">
                              <span
                                className=" bg-light fw-semibold px-3 py-2 rounded-3"
                              >
                                Question {questionNumber}
                              </span>
                              <div>
                                <h5 className="fw-semibold mb-0">
                                  {q.label || q.questionText || `Question ${questionNumber}`}
                                </h5>
                              </div>
                            </div>
                            {(q.showInfo || (q.infoHtml && String(q.infoHtml).trim())) && q.infoPlacement === "before" && (
                              <div className="mb-3 p-3 rounded-3 bg-light small eval-rich-content" style={{ lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: q.infoHtml || "" }} />
                            )}
                            {/* Preview for each question type */}
                            <div className="p-3 rounded" style={{ border: "2px dashed var(--gray-200)" }}>
                              <div className="d-flex flex-row gap-3 align-items-center justify-content-between mb-3">
                                <small className="text-muted">Preview:</small>
                                <small className="text-muted"><strong>Type:</strong> {q.type}</small>
                              </div>
                          {q.type === "header-paragraph" && (
                            <div style={{ padding: "10px 0" }}>
                              <div style={{ fontWeight: "bold", fontSize: 24, marginBottom: 8 }}>{q.header || "Header Text"}</div>
                              <div style={{ color: "#555" }}>{q.paragraph || "Paragraph text goes here..."}</div>
                            </div>
                          )}
                          {q.type === "divider" && (
                            <hr style={{ 
                              border: "none",
                              borderTop: `2px ${q.style || "solid"} #ccc`,
                              margin: "20px 0"
                            }} />
                          )}
                              {q.type === "text" && (
                                <div className="p-3 rounded-3 bg-light">Single line text input</div>
                              )}

                              {q.type === "textarea" && (
                                <div className="p-3 rounded-3 bg-light">Paragraph text input</div>
                              )}

                              {q.type === "radio-matrix" && (
                                <div className="table-responsive custom-modern-responsive">
                                  <Table>
                                    <thead>
                                      <tr>
                                        <th className="px-3">Performance Factor</th>
                                        {(q.columns && q.columns.length > 0 ? q.columns : [
                                          "Unsatisfactory",
                                          "Needs Improvement",
                                          "Meets Expectations",
                                          "Exceeds Expectations"
                                        ]).map((col, colIdx) => (
                                          <th key={colIdx}>{col}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {q.options.map((opt, idx) => (
                                        <tr key={idx}>
                                          <td className="px-3">{opt}</td>
                                          {(q.columns && q.columns.length > 0 ? q.columns : [
                                            "Unsatisfactory",
                                            "Needs Improvement",
                                            "Meets Expectations",
                                            "Exceeds Expectations"
                                          ]).map((_, colIdx) => (
                                            <td key={colIdx}>
                                              <div
                                                className="rounded-circle"
                                                style={{
                                                  width: 16,
                                                  height: 16,
                                                  margin: "0 auto",
                                                  border: "1px solid var(--gray-400)",
                                                }}
                                              >
                                                <span
                                                  style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    margin: '3px auto',
                                                    width: '8px',
                                                    borderRadius: '50%',
                                                    height: '8px',
                                                  }}
                                                > 
                                                </span>
                                              </div>
                                            </td>
                                          ))}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </div>
                              )}

                              {q.type === "checkbox" && (
                                <div className="d-flex flex-column gap-2">
                                  {q.options.map((opt, j) => (
                                    <Button
                                      key={j}
                                      variant="light-solid"
                                      className="p-3 d-flex align-items-center gap-2"
                                      disabled
                                    >
                                      <Form.Check
                                        type="checkbox"
                                        disabled
                                        onChange={() => {}}
                                        style={{ pointerEvents: "none" }}
                                      />
                                      {opt}
                                    </Button>
                                  ))}
                                </div>
                              )}

                              {q.type === "radio" && (
                                <div className="d-flex flex-column gap-2">
                                  {q.options.map((opt, j) => (
                                    <Button
                                      key={j}
                                      variant="light-solid"
                                      className="p-3 d-flex align-items-center gap-2"
                                      disabled
                                    >
                                      <Form.Check
                                        type="radio"
                                        label={<span>{opt}</span>}
                                        disabled
                                        className="mb-0"
                                      />
                                    </Button>
                                  ))}
                                </div>
                              )}

                              {q.type === "dropdown" && (
                                <Form.Select disabled>
                                  <option value="">Select an option</option>
                                  {q.options.map((opt, j) => (
                                    <option key={j} value={opt}>{opt}</option>
                                  ))}
                                </Form.Select>
                              )}

                              {q.type === "rating" && (
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  {getRatingOptions(q).map((opt) => (
                                    <Button
                                      key={String(opt.value)}
                                      variant="light-solid"
                                      className="p-3 d-flex align-items-center gap-2 w-100"
                                      disabled
                                    >
                                      <Form.Check
                                        type="radio"
                                        disabled
                                        className="m-0"
                                      />
                                      <span>{opt.label}</span>
                                    </Button>
                                  ))}
                                </div>
                              )}
                              {(q.showInfo || (q.infoHtml && String(q.infoHtml).trim())) && q.infoPlacement === "after" && (
                                <div className="mt-3 p-3 rounded-3 bg-light small eval-rich-content" style={{ lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: q.infoHtml || "" }} />
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </Card.Body>
              <Card.Footer
                className="d-flex justify-content-end py-3 px-4"
                style={{
                  borderTop: "1px dashed var(--border-bg)",
                  backgroundColor: "var(--bs-card-bg)",
                }}
              >
                <Button variant="primary" onClick={() => navigate("/evaluations")}>
                  Back to Evaluations
                </Button>
              </Card.Footer>
            </Card>
            
            {/* Assigned Residents Card */}
            {template.assignedResidents && template.assignedResidents.length > 0 && (
              <Card className="mb-4 shadow-sm border-0">
                <Card.Header className="px-4 py-3 border-0" style={{backgroundColor: "var(--bs-card-bg)"}}>
                  <h5 className="fw-semibold mb-0">Assigned Residents</h5>
                </Card.Header>
                <Card.Body className="px-4">
                  <ul className="mb-0 custom-list">
                    {template.assignedResidents.slice(0, 4).map((id, index) => {
                      const resident = allResidents.find(r => r.id === id);
                      return (
                        <li key={index} className="text-muted mb-2">
                          {resident ? `${resident.firstName} ${resident.lastName}` : `Resident ID: ${id}`}
                        </li>
                      );
                    })}
                    {template.assignedResidents.length > 4 && (
                      <li className="text-primary" style={{cursor: 'pointer'}} onClick={() => setShowResidentsModal(true)}>
                        View All ({template.assignedResidents.length})
                      </li>
                    )}
                  </ul>
                  <Modal show={showResidentsModal} onHide={() => setShowResidentsModal(false)} size="md">
                    <Modal.Header closeButton>
                      <Modal.Title>Assigned Residents</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <ul className="list-unstyled">
                        {template.assignedResidents.map((id, index) => {
                          const resident = allResidents.find(r => r.id === id);
                          return (
                            <li key={index} className="mb-2">
                              <span className="fw-semibold">{resident ? `${resident.firstName} ${resident.lastName}` : `Resident ID: ${id}`}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </Modal.Body>
                    <Modal.Footer>
                      <Button variant="secondary" onClick={() => setShowResidentsModal(false)}>
                        Close
                      </Button>
                    </Modal.Footer>
                  </Modal>
                </Card.Body>
              </Card>
            )}


          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EvaluationTemplateViewer;
